import {
  Paper,
  Typography,
  Box,
  Grid,
  Stack,
} from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  ResponsiveContainer,
} from "recharts";

const EMPTY_PLACEHOLDER = "-";

const normalizeSeries = (series) => {
  if (!Array.isArray(series)) return [];
  return series
    .map((point) => {
      if (point == null) return null;
      if (typeof point === "number") return { value: point };
      const ts =
        point.timestamp ||
        point.time ||
        point.t ||
        point.Timestamp ||
        null;
      const value =
        point.value ??
        point.Average ??
        point.avg ??
        point.v ??
        null;
      if (value == null) return null;
      return {
        timestamp: ts ? new Date(ts).getTime() : undefined,
        value: Number(value),
      };
    })
    .filter(Boolean);
};

const lastValue = (data) =>
  data.length > 0 ? data[data.length - 1].value : null;

const formatNumber = (n, digits = 0) => {
  if (n == null || Number.isNaN(n)) return EMPTY_PLACEHOLDER;
  return Number(n).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

const MetricTile = ({ title, data, unit, color = "#D0749B", digits = 0 }) => {
  const current = lastValue(data);
  const hasData = data.length > 0;

  return (
    <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
      <Typography variant="caption" color="text.secondary">
        {title}
      </Typography>
      <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mt: 0.5 }}>
        <Typography variant="h5">
          {hasData ? formatNumber(current, digits) : EMPTY_PLACEHOLDER}
        </Typography>
        {hasData && unit && (
          <Typography variant="caption" color="text.secondary">
            {unit}
          </Typography>
        )}
      </Stack>
      <Box sx={{ width: "100%", height: 80, mt: 1 }}>
        {hasData ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <XAxis dataKey="timestamp" hide />
              <YAxis hide domain={["auto", "auto"]} />
              <ReTooltip
                formatter={(v) => formatNumber(v, digits)}
                labelFormatter={(l) =>
                  l ? new Date(l).toLocaleTimeString() : ""
                }
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke={color}
                dot={false}
                strokeWidth={2}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "text.secondary",
            }}
          >
            <Typography variant="body2">{EMPTY_PLACEHOLDER}</Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

const AlertsTile = ({ count }) => {
  const hasAlerts = typeof count === "number" && count > 0;
  return (
    <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
      <Typography variant="caption" color="text.secondary">
        Active Alerts
      </Typography>
      <Box
        sx={{
          mt: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 100,
        }}
      >
        <Typography
          variant="h2"
          sx={{ color: hasAlerts ? "error.main" : "text.primary" }}
        >
          {typeof count === "number" ? count : EMPTY_PLACEHOLDER}
        </Typography>
      </Box>
    </Paper>
  );
};

const MetricsPanel = ({ metrics, channelState }) => {
  const networkIn = normalizeSeries(
    metrics?.networkIn || metrics?.NetworkIn || metrics?.bitrateIn
  );
  const networkOut = normalizeSeries(
    metrics?.networkOut || metrics?.NetworkOut || metrics?.bitrateOut
  );
  const frameRate = normalizeSeries(
    metrics?.inputVideoFrameRate ||
      metrics?.InputVideoFrameRate ||
      metrics?.frameRate
  );
  const activeAlerts =
    metrics?.activeAlerts ?? metrics?.ActiveAlerts ?? null;

  const isInactive =
    metrics == null ||
    channelState === "IDLE" ||
    channelState === "CREATING" ||
    channelState === "DELETED";

  return (
    <Paper sx={{ p: 3, position: "relative" }}>
      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        Metricas (ultimos 5 min)
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <MetricTile
            title="Bitrate In"
            data={networkIn}
            unit="bps"
            color="#2e7d32"
            digits={0}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MetricTile
            title="Bitrate Out"
            data={networkOut}
            unit="bps"
            color="#1976d2"
            digits={0}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MetricTile
            title="Frame Rate"
            data={frameRate}
            unit="fps"
            color="#ed6c02"
            digits={1}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <AlertsTile count={activeAlerts} />
        </Grid>
      </Grid>

      {isInactive && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255,255,255,0.75)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Canal inactivo - metricas no disponibles
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default MetricsPanel;
